import { useEffect, useState } from "react";
import {
  Card,
  Container,
  Group,
  Text,
  Table,
  Badge,
  ActionIcon,
  TextInput,
  Pagination,
  Button,
  Modal,
  Stack,
  Alert,
  Select,
  Loader,
  ScrollArea,
} from "@mantine/core";
import { useForm } from "@mantine/form";
import { useMutation, useQuery } from "@tanstack/react-query";
import {
  IconEye,
  IconRocket,
  IconAlertCircle,
  IconEdit,
  IconCaretDownFilled,
  IconCaretUpFilled,
} from "@tabler/icons-react";
import { showSuccess } from "../lib/notifications";
import { deploymentsApi } from "../lib/api";
import { DeploymentDetailsModal } from "../components/DeploymentDetailsModal";
import { Deployment } from "../types";
import { LoadingOverlay } from "../components/LoadingOverlay";
import { PageHeader } from "../components/PageHeader";
import { PageTransition } from "../components/PageTransition";

export function DeploymentsPage() {
  const [deployments, setDeployments] = useState<Deployment[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [limit] = useState(20);
  // Removed selection for prod promotion
  const [selectedDeployment, setSelectedDeployment] =
    useState<Deployment | null>(null);
  const [modalOpened, setModalOpened] = useState<boolean>(false);
  const [prodDeployModalOpen, setProdDeployModalOpen] =
    useState<boolean>(false);
  const [prodDeployError, setProdDeployError] = useState<string>("");
  const [selectedRepo, setSelectedRepo] = useState<string>("");
  const [editShaMode, setEditShaMode] = useState<boolean>(false);
  const [repoDropdownOpen, setRepoDropdownOpen] = useState(false);
  const [branchDropdownOpen, setBranchDropdownOpen] = useState(false);

  const prodDeployForm = useForm({
    initialValues: {
      repository: "",
      branch: "",
      commitSha: "",
    },
    validate: {
      repository: (value) => (!value ? "Repository is required" : null),
      commitSha: (value) => (!value ? "Commit SHA is required" : null),
      branch: (value) => (!value ? "Branch is required" : null),
    },
  });

  const { data: repos = [], isLoading: reposLoading } = useQuery({
    queryKey: ["github-repositories"],
    queryFn: async () => {
      const res = await deploymentsApi.getRepositories();
      return res.data.repositories;
    },
  });

  const { data: branches = [], isLoading: branchesLoading } = useQuery({
    queryKey: ["deployment-branches", selectedRepo],
    queryFn: async () => {
      if (!selectedRepo) return [];
      const res = await deploymentsApi.getBranches(selectedRepo);
      return res.data.branches;
    },
    enabled: !!selectedRepo,
  });

  useEffect(() => {
    setLoading(true);
    deploymentsApi
      .list()
      .then((res) => setDeployments(res.data.items as any))
      .catch(() => setDeployments([]))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    setPage(1);
  }, [search]);

  // Removed proceed-to-prod functionality for now

  // Removed global Refresh to avoid refreshing all rows; modal handles per-item refresh

  const openDeploymentModal = (deployment: Deployment) => {
    setSelectedDeployment(deployment);
    setModalOpened(true);
  };

  const closeDeploymentModal = () => {
    setModalOpened(false);
    setSelectedDeployment(null);
  };

  const handleDeploymentUpdate = (updatedDeployment: Deployment) => {
    setDeployments((prev) =>
      prev.map((d) => (d.id === updatedDeployment.id ? updatedDeployment : d))
    );
    setSelectedDeployment(updatedDeployment);
  };

  // Filter deployments based on search
  const filteredDeployments = deployments.filter((deployment) =>
    search
      ? deployment.serviceName.toLowerCase().includes(search.toLowerCase()) ||
        deployment.repo.toLowerCase().includes(search.toLowerCase()) ||
        deployment.environment.toLowerCase().includes(search.toLowerCase())
      : true
  );

  // Calculate paginated deployments
  const totalPages = Math.ceil((filteredDeployments?.length || 0) / limit);
  const paginatedDeployments = filteredDeployments?.slice(
    (page - 1) * limit,
    page * limit
  );

  // Mutation for creating production deployments
  const createProductionDeployments = useMutation({
    mutationFn: async (values: { repository: string; commitSha: string }) => {
      const response = await deploymentsApi.createProductionDeployments(
        values.repository,
        values.commitSha
      );
      return response.data;
    },
    onSuccess: () => {
      setProdDeployModalOpen(false);
      setProdDeployError("");
      prodDeployForm.reset();
      showSuccess(
        "Production builds initiated successfully. Deployment will happen automatically once builds complete."
      );
      // Refresh deployments list
      setLoading(true);
      deploymentsApi
        .list()
        .then((res) => setDeployments(res.data.items as any))
        .catch(() => setDeployments([]))
        .finally(() => setLoading(false));
    },
    onError: (error: any) => {
      setProdDeployError(
        error.response?.data?.error || "Failed to create production deployments"
      );
    },
  });

  return (
    <Container fluid p={0} pos="relative">
      <PageTransition>
        <PageHeader
          title="Deployments"
          description="View recent deployments and their status"
        />

        {loading ? (
          <LoadingOverlay message="Loading..." />
        ) : paginatedDeployments.length === 0 ? (
          <Text color="dimmed" mt="md">
            No deployments found.
          </Text>
        ) : (
          <Card mt="md" shadow="sm" radius="md" withBorder>
            <Group p="md" position="apart" align="center">
              <TextInput
                placeholder="Search deployments..."
                value={search}
                onChange={(e) => setSearch(e.currentTarget.value)}
                sx={(theme) => ({
                  width: 300,
                  [theme.fn.smallerThan('sm')]: { width: '100%' },
                })}
              />
              <Button
                leftIcon={<IconRocket size={16} />}
                onClick={() => setProdDeployModalOpen(true)}
                variant="filled"
              >
                Create Production Deployment
              </Button>
            </Group>

            <ScrollArea>
              <Table striped highlightOnHover>
                <thead>
                  <tr>
                    <th>Service</th>
                    <th>Repository</th>
                    <th>Environment</th>
                    <th>Commit</th>
                    <th>Started</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedDeployments.map((d) => (
                    <tr
                      key={d.id}
                      style={{ cursor: "pointer" }}
                      onClick={() => openDeploymentModal(d)}
                    >
                      <td>
                        <Text weight={500}>{d.serviceName}</Text>
                      </td>
                      <td>
                        <Text size="sm">{d.repo}</Text>
                      </td>
                      <td>
                        <Badge color={d.environment === "PROD" ? "red" : "blue"}>
                          {d.environment}
                        </Badge>
                      </td>
                      <td>
                        <Text size="sm" weight={500}>
                          <code>{d.commitSha.slice(0, 7)}</code>
                        </Text>
                      </td>
                      <td>
                        <Text size="sm">
                          {new Date(d.createdAt).toLocaleString()}
                        </Text>
                      </td>
                      <td>
                        <ActionIcon
                          variant="subtle"
                          onClick={(e) => {
                            e.stopPropagation();
                            openDeploymentModal(d);
                          }}
                          title="View Details"
                        >
                          <IconEye size={16} />
                        </ActionIcon>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </ScrollArea>

            {(filteredDeployments?.length ?? 0) > 0 && (
              <Group position="center" mt="xl" mb="md">
                <Pagination
                  total={totalPages}
                  value={page}
                  onChange={setPage}
                  size="sm"
                />
              </Group>
            )}
          </Card>
        )}

        <DeploymentDetailsModal
          deployment={selectedDeployment}
          opened={modalOpened}
          onClose={closeDeploymentModal}
          onDeploymentUpdate={handleDeploymentUpdate}
        />

        {/* Production Deployment Modal */}
        <Modal
          opened={prodDeployModalOpen}
          onClose={() => {
            setProdDeployModalOpen(false);
            setProdDeployError("");
            prodDeployForm.reset();
            setSelectedRepo("");
            setEditShaMode(false);
          }}
          title="Create Production Deployment"
          size="xl"
        >
          {prodDeployError && (
            <Alert
              color="red"
              title="Error"
              icon={<IconAlertCircle size={16} />}
              mb="md"
            >
              {prodDeployError}
            </Alert>
          )}

          <form
            onSubmit={prodDeployForm.onSubmit((values) =>
              createProductionDeployments.mutate(values)
            )}
          >
            <Stack spacing="xl">
              <Text size="sm" color="dimmed">
                Select a repository and commit SHA to trigger production builds
                for all associated services.
              </Text>

              <Select
                label="Repository"
                placeholder="Select or type repository name"
                description="repository name (e.g., repository-name)"
                required
                data={repos.map((repo) => ({
                  value: repo,
                  label: repo,
                }))}
                searchable
                nothingFound="No repositories found"
                disabled={reposLoading}
                value={prodDeployForm.values.repository}
                maxDropdownHeight={150}
                rightSection={
                  repoDropdownOpen ? (
                    <IconCaretUpFilled size={16} />
                  ) : (
                    <IconCaretDownFilled size={16} />
                  )
                }
                onDropdownOpen={() => setRepoDropdownOpen(true)}
                onDropdownClose={() => setRepoDropdownOpen(false)}
                onChange={(value) => {
                  prodDeployForm.setFieldValue("repository", value || "");
                  setSelectedRepo(value || "");
                  prodDeployForm.setFieldValue("branch", "");
                  prodDeployForm.setFieldValue("commitSha", "");
                  setEditShaMode(false);
                }}
              />

              {!editShaMode ? (
                <Group align="end">
                  <Select
                    label="Branch"
                    placeholder={
                      !selectedRepo
                        ? "Select a repository first"
                        : branchesLoading
                        ? "Loading the branches..."
                        : "Select or type branch name"
                    }
                    description="Select a branch to deploy"
                    required
                    data={branches.map((branch) => ({
                      value: branch.name,
                      label: branch.name,
                    }))}
                    searchable
                    nothingFound={
                      branchesLoading ? "Loading..." : "No branches found"
                    }
                    disabled={branchesLoading || !selectedRepo}
                    style={{ flex: 1 }}
                    value={prodDeployForm.values.branch}
                    maxDropdownHeight={150}
                    rightSection={
                      branchesLoading ? (
                        <Loader size="sm" />
                      ) : branchDropdownOpen ? (
                        <IconCaretUpFilled size={16} />
                      ) : (
                        <IconCaretDownFilled size={16} />
                      )
                    }
                    onDropdownOpen={() => setBranchDropdownOpen(true)}
                    onDropdownClose={() => setBranchDropdownOpen(false)}
                    onChange={(value) => {
                      prodDeployForm.setFieldValue("branch", value || "");
                      const selectedBranch = branches.find(
                        (b) => b.name === value
                      );
                      if (selectedBranch) {
                        prodDeployForm.setFieldValue(
                          "commitSha",
                          selectedBranch.sha
                        );
                      }
                    }}
                  />
                  <ActionIcon
                    variant="subtle"
                    onClick={() => setEditShaMode(true)}
                    title="Edit SHA directly"
                    style={{ marginBottom: 4 }}
                  >
                    <IconEdit size={22} />
                  </ActionIcon>
                </Group>
              ) : (
                <Group align="end">
                  <TextInput
                    label="Commit SHA"
                    placeholder="abc123def456..."
                    description="The commit SHA to deploy to production"
                    required
                    style={{ flex: 1 }}
                    {...prodDeployForm.getInputProps("commitSha")}
                  />
                  <ActionIcon
                    variant="subtle"
                    onClick={() => setEditShaMode(false)}
                    title="Select branch"
                    style={{ marginBottom: 4 }}
                  >
                    <IconEdit size={22} />
                  </ActionIcon>
                </Group>
              )}

              <Button
                type="submit"
                loading={createProductionDeployments.isLoading}
                fullWidth
                leftIcon={<IconRocket size={16} />}
              >
                Deploy to Production
              </Button>
            </Stack>
          </form>
        </Modal>
      </PageTransition>
    </Container>
  );
}
